import { Auth } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import Row from '../../components/Row';
import Col from '../../components/Col';
import TextInput from '../../components/TextInput';

import useValidationMessages from '../../helpers/useValidationMessages';
import Button from '../../components/Button';
import CenteredPage from '../../containers/CenteredPage';
import Text from '../../components/Text';
import Box from '../../components/layout/Box';

const ResetPassword = () => {
  // Send confirmation code to user's email

  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const [updatePasswordError, setUpdatePasswordError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const username = location?.state?.email;
  const fromLogin = location?.state?.fromLogin;

  useEffect(() => {
    if (!username) {
      navigate('/');
    }
  }, []);

  const handleSentResetLink = async () => {
    if (!username) return;

    Auth.forgotPassword(username).catch((err) => console.log(err));
  };

  const handleUpdatePassword = ({ code, password }) => {
    setUpdatingPassword(true);

    Auth.forgotPasswordSubmit(username, code, password)
      .then(() => {
        setUpdatingPassword(false);
        setPasswordUpdated(true);
      })
      .catch((err) => {
        console.log(err);
        setUpdatePasswordError(err.code);
        setUpdatingPassword(false);
      });
  };

  const { required, tooShortPassword } = useValidationMessages();

  useEffect(() => {
    if (username && fromLogin) {
      handleSentResetLink();
    }
  }, []);

  const initialValues = {
    code: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(required),
    password: Yup.string().min(8, tooShortPassword).required(required),
  });

  return (
    <CenteredPage
      style={{
        maxWidth: 600,
      }}
    >
      <Row className="px-3 py-3 justify-content-center">
        <Col style={{ maxWidth: 700 }}>
          {fromLogin ? (
            <Box className="flex-column mb-3">
              <h2>
                <FormattedMessage defaultMessage="Åtgärd krävs" />
              </h2>

              <Box>
                <Text className="mt-2">
                  <FormattedMessage
                    defaultMessage="
                    Sedan du senast loggade in har vi uppgraderat vår plattform. För
                    din säkerhet måste du välja ett nytt lösenord."
                  />
                </Text>
              </Box>

              <Box>
                <Text className="mt-2">
                  <FormattedMessage
                    values={{
                      email: (
                        <Text variant="body" style={{ fontWeight: 600 }}>
                          {username}
                        </Text>
                      ),
                    }}
                    defaultMessage=" Vi har skickat ett
                    e-postmeddelande till {email} med en kod du kan använda
                    för att byta lösenord."
                  />
                </Text>
              </Box>
            </Box>
          ) : (
            <>
              <h2>
                <FormattedMessage defaultMessage="Välj nytt lösenord" />
              </h2>
              <div className="mb-3">
                <FormattedMessage
                  values={{ email: username }}
                  defaultMessage="En kod har skickats till {email}. Ange koden och välj ett nytt lösenord."
                />
              </div>
            </>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleUpdatePassword}
          >
            {({ values, errors, handleChange, handleBlur, submitCount }) => {
              const showErrors = submitCount > 0;

              return (
                <FormikForm>
                  <TextInput
                    type="text"
                    name="code"
                    onChange={handleChange('code')}
                    onBlur={handleBlur}
                    value={values.code}
                    error={showErrors && errors.code}
                    placeholder={<FormattedMessage defaultMessage="Kod" />}
                    autoComplete="new-password"
                  />

                  <TextInput
                    type="password"
                    name="password"
                    onChange={handleChange('password')}
                    onBlur={handleBlur}
                    value={values.password}
                    error={showErrors && errors.password}
                    placeholder={<FormattedMessage defaultMessage="Lösenord" />}
                    autoComplete="new-password"
                  />

                  {!!updatePasswordError && ( // === 'CodeMismatchException' && (
                    <div>
                      <FormattedMessage defaultMessage="Felaktig kod" />
                    </div>
                  )}

                  {passwordUpdated && (
                    <div className="mb-3">
                      <FormattedMessage defaultMessage="Ditt lösenord har uppdaterats. Du ska nu kunna logga in igen." />
                    </div>
                  )}

                  <div className="d-flex w-100 justify-content-end">
                    {passwordUpdated && (
                      <Button
                        backgroundColor="transparentColor"
                        titleColor="primaryColor"
                        onClick={() => {
                          navigate('/sign-in');
                        }}
                      >
                        <FormattedMessage defaultMessage="Till inloggning" />
                      </Button>
                    )}

                    <Button type="submit" isLoading={updatingPassword}>
                      <FormattedMessage defaultMessage="Sätt nytt lösenord" />
                    </Button>
                  </div>
                </FormikForm>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </CenteredPage>
  );
};

export default ResetPassword;
